import { useState, useEffect, useCallback } from 'react';
import { X, Calendar, ClipboardCheck, Send } from 'lucide-react';

const BookingModal = ({ isOpen, onClose, serviceType = 'reservation' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    serviceType: serviceType,
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      setFormData(prev => ({ ...prev, serviceType }));
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleEscKey, serviceType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const subject = formData.serviceType === 'estimation' 
      ? 'Demande d\'Estimation - Lavage de Vitre'
      : 'Demande de Réservation - Lavage de Vitre';

    const body = `
Nom: ${formData.name}
Téléphone: ${formData.phone}
Email: ${formData.email}
Adresse: ${formData.address}
Date préférée: ${formData.preferredDate || 'Non spécifiée'}
Heure préférée: ${formData.preferredTime || 'Non spécifiée'}
Type de service: ${formData.serviceType === 'estimation' ? 'Estimation' : 'Réservation'}

Message:
${formData.message || 'Aucun message supplémentaire'}

---
Envoyé depuis lavagedevitre.org
    `.trim();

    const mailtoLink = `mailto:Lavagedevitreboisfranc@live.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    window.location.href = mailtoLink;

    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        preferredDate: '',
        preferredTime: '',
        serviceType: serviceType,
        message: ''
      });
      onClose();
    }, 1000);
  };

  if (!isOpen) return null;

  const isEstimation = formData.serviceType === 'estimation';

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="booking-modal-close" onClick={onClose}>
          <X size={24} />
        </button>
        
        <div className="booking-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
            {isEstimation ? <ClipboardCheck size={28} /> : <Calendar size={28} />}
            <h2>{isEstimation ? 'Demande d\'Estimation' : 'Réservation en Ligne'}</h2>
          </div>
          <p>Remplissez le formulaire et nous vous contacterons rapidement</p>
        </div>

        <form onSubmit={handleSubmit} className="booking-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nom complet *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Jean Dupont"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Téléphone *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="514-XXX-XXXX"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="exemple@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">Adresse du service *</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="123 Rue Example, Bois-Franc, QC"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="preferredDate">Date préférée</label>
              <input
                type="date"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label htmlFor="preferredTime">Heure préférée</label>
              <select
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
              >
                <option value="">Sélectionner...</option>
                <option value="8h-10h">8h - 10h</option>
                <option value="10h-12h">10h - 12h</option>
                <option value="12h-14h">12h - 14h</option>
                <option value="14h-16h">14h - 16h</option>
                <option value="16h-18h">16h - 18h</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message / Détails supplémentaires</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              placeholder="Nombre de fenêtres, étages, accès difficile, etc."
            ></textarea>
          </div>

          <button 
            type="submit" 
            className="booking-submit-btn"
            disabled={isSubmitting}
          >
            <Send size={18} />
            <span>{isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
